import { centsToFormattedDollars } from '@fruitful/fruitils';
import React, { type ReactNode } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    EditIcon,
    FruitfulCustomIcon,
    GripVerticalIcon,
    Icon,
    InstitutionLogo,
    MonoLabelRegular,
    redactPrivate,
    View,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import {
    type AccountSummary,
    type BankAccountDisplayDetail,
    type DepositAccount,
    type IncomeSplitReviewContext,
    type IncomeSplitRuleLegacy,
    IncomeSplitRuleType,
    type PaycheckRoutingRuleAccountMetadata,
    PaycheckRoutingRuleAccountType,
    type ProposedAccount,
} from '../types/IncomeSplit';
import {
    getAccountLogo,
    getStringAccountProperty,
} from '../utils/incomeSplitAccountUtils';
import {
    calculateFinalizedAmount,
    hasConditions,
} from '../utils/incomeSplitUtils';

const getLastFour = (accountNumber: string | null | undefined) => {
    const trimmedAccountNumber = accountNumber?.trim();
    if (trimmedAccountNumber == null || trimmedAccountNumber.length === 0) {
        return null;
    }

    return trimmedAccountNumber.slice(-4);
};

const formatAccountNameWithLastFour = (
    accountName: string,
    accountNumber: string | null | undefined,
) => {
    const lastFour = getLastFour(accountNumber);
    return lastFour == null ? accountName : `${accountName} (${lastFour})`;
};

const RuleDataTag = ({
    text,
    color,
    isPrivate = false,
}: {
    text: string;
    color: string;
    isPrivate?: boolean;
}) => {
    return (
        <View
            alignSelf="flex-start"
            backgroundColor="$gray200"
            borderRadius={16}
            paddingVertical={4}
            paddingHorizontal={6}
            {...(isPrivate && redactPrivate())}
        >
            <MonoLabelRegular color={color}>{text}</MonoLabelRegular>
        </View>
    );
};

const RuleDataRow = ({ children }: { children: ReactNode }) => {
    return (
        <XStack gap={8} alignItems="center" flexWrap="wrap">
            <>{children}</>
        </XStack>
    );
};

const RuleData = ({ rule }: { rule: IncomeSplitRuleLegacy }) => {
    const {
        type,
        amount,
        monthlyContributionMaxCents,
        yearlyContributionMaxCents,
    } = rule;

    const isMinimumBalanceRule = type === IncomeSplitRuleType.MINIMUM_BALANCE;
    const hasMonthlyContributionMax = (monthlyContributionMaxCents ?? 0) > 0;
    const hasYearlyContributionMax = (yearlyContributionMaxCents ?? 0) > 0;

    return (
        <YStack padding={12} borderRadius={8} backgroundColor="$white" gap={8}>
            <RuleDataRow>
                <RuleDataTag text="CONTRIBUTE" color="$gray1200" />
                <RuleDataTag
                    text={calculateFinalizedAmount(rule)}
                    color="$green900"
                />
            </RuleDataRow>
            {hasConditions(rule) && (
                <YStack>
                    {isMinimumBalanceRule ? (
                        <RuleDataRow>
                            <RuleDataTag text="UNTIL" color="$gray1200" />
                            <MonoLabelRegular color="$gray1200">
                                available balance
                            </MonoLabelRegular>
                            <MonoLabelRegular color="$gray1200">
                                =
                            </MonoLabelRegular>
                            <RuleDataTag
                                text={centsToFormattedDollars(amount, false)}
                                color="$green900"
                                isPrivate
                            />
                        </RuleDataRow>
                    ) : null}
                    {hasMonthlyContributionMax ? (
                        <>
                            {isMinimumBalanceRule && (
                                <MonoLabelRegular
                                    paddingVertical={8}
                                    color="$gray1200"
                                >
                                    or
                                </MonoLabelRegular>
                            )}
                            <RuleDataRow>
                                <RuleDataTag text="UNTIL" color="$gray1200" />
                                <MonoLabelRegular color="$gray1200">
                                    monthly contribution
                                </MonoLabelRegular>
                                <MonoLabelRegular color="$gray1200">
                                    =
                                </MonoLabelRegular>
                                <RuleDataTag
                                    text={centsToFormattedDollars(
                                        monthlyContributionMaxCents ?? 0,
                                        false,
                                    )}
                                    color="$green900"
                                    isPrivate
                                />
                            </RuleDataRow>
                        </>
                    ) : null}
                    {hasYearlyContributionMax ? (
                        <>
                            {(isMinimumBalanceRule ||
                                hasMonthlyContributionMax) && (
                                <MonoLabelRegular
                                    paddingVertical={8}
                                    color="$gray1200"
                                >
                                    or
                                </MonoLabelRegular>
                            )}
                            <RuleDataRow>
                                <RuleDataTag text="UNTIL" color="$gray1200" />
                                <MonoLabelRegular color="$gray1200">
                                    annual contribution
                                </MonoLabelRegular>
                                <MonoLabelRegular color="$gray1200">
                                    =
                                </MonoLabelRegular>
                                <RuleDataTag
                                    text={centsToFormattedDollars(
                                        yearlyContributionMaxCents ?? 0,
                                        false,
                                    )}
                                    color="$green900"
                                    isPrivate
                                />
                            </RuleDataRow>
                        </>
                    ) : null}
                </YStack>
            )}
        </YStack>
    );
};

export const IncomeSplitRuleCard = ({
    rule,
    account,
    color,
    icon,
    context,
    onPressEdit,
    onPressClose,
    dragHandleProps,
}: {
    rule: IncomeSplitRuleLegacy;
    account:
        | PaycheckRoutingRuleAccountMetadata
        | BankAccountDisplayDetail
        | DepositAccount
        | ProposedAccount
        | AccountSummary;
    color?: string | null;
    icon?: string | null;
    context: IncomeSplitReviewContext;
    onPressEdit?: () => void;
    onPressClose?: () => void;
    dragHandleProps?: Record<string, unknown>;
}) => {
    const isExternal =
        rule.accountType !== undefined &&
        rule.accountType === PaycheckRoutingRuleAccountType.EXTERNAL;

    let accountName;
    let institutionName: string | null = null;
    if (isExternal) {
        accountName = formatAccountNameWithLastFour(
            getStringAccountProperty(account, 'nickname') ??
                getStringAccountProperty(account, 'name') ??
                'External Account',
            getStringAccountProperty(account, 'accountNumberMasked') ??
                getStringAccountProperty(account, 'accountNumber') ??
                null,
        );
        institutionName =
            getStringAccountProperty(account, 'institutionName') ??
            getStringAccountProperty(account, 'bankName') ??
            null;
    } else {
        accountName =
            rule.accountMetadata?.nickname ??
            getStringAccountProperty(account, 'nickname') ??
            getStringAccountProperty(account, 'name') ??
            'Account';
    }

    return (
        <XStack
            paddingTop={context === 'review' ? 16 : 8}
            paddingBottom={16}
            paddingRight={16}
            paddingLeft={context === 'review' ? 16 : 0}
            borderRadius={16}
            backgroundColor="$gray200"
            alignItems="center"
        >
            {context === 'edit' && (
                <View cursor="grab" {...dragHandleProps}>
                    <Icon icon={GripVerticalIcon} color="$gray1000" />
                </View>
            )}
            <YStack flexGrow={1} flexShrink={1} gap={16}>
                <XStack alignItems="center" justifyContent="space-between">
                    <XStack gap={12} alignItems="center">
                        {isExternal ? (
                            <InstitutionLogo
                                size="xxs"
                                logo={getAccountLogo(account)}
                            />
                        ) : (
                            <FruitfulCustomIcon
                                icon={icon}
                                color={color}
                                size="xxs"
                                useAltColors={
                                    rule.accountType ===
                                    PaycheckRoutingRuleAccountType.INTERNAL_INVESTMENT
                                }
                            />
                        )}
                        <YStack gap={4}>
                            <BodyMediumRegular>{accountName}</BodyMediumRegular>
                            {institutionName != null && (
                                <BodySmallRegular color="$gray1000">
                                    {institutionName}
                                </BodySmallRegular>
                            )}
                        </YStack>
                    </XStack>
                    {context === 'edit' && (
                        <XStack alignItems="center">
                            <View
                                alignItems="center"
                                justifyContent="center"
                                animation="button"
                                pressStyle={{ scale: 0.95 }}
                                onPress={onPressEdit}
                                cursor="pointer"
                                width={40}
                                height={40}
                            >
                                <Icon icon={EditIcon} color="$black" />
                            </View>
                            <View
                                alignItems="center"
                                justifyContent="center"
                                animation="button"
                                pressStyle={{ scale: 0.95 }}
                                onPress={onPressClose}
                                cursor="pointer"
                                width={40}
                                height={40}
                            >
                                <Icon icon={XIcon} />
                            </View>
                        </XStack>
                    )}
                </XStack>
                <RuleData rule={rule} />
            </YStack>
        </XStack>
    );
};

export default IncomeSplitRuleCard;
